/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { PriceComponent } from '@msdyn365-commerce/components';
import { ICoreContext, ITelemetry } from '@msdyn365-commerce/core';
import { ICheckoutState, IGiftCardExtend } from '@msdyn365-commerce/global-state';
import { Cart, ChannelConfiguration, ChannelDeliveryOptionConfiguration } from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';

export interface IOrderSummaryProps {
    subTotalLabel: string;
    shippingLabel: string;
    taxLabel: string;
    orderTotalLabel: string;
    loyaltyLabel?: string;
    giftcardLabel?: string;
    totalDiscountsLabel: string;
    context: ICoreContext;
    telemetry: ITelemetry;
    cart: Cart;
    channelConfiguration: ChannelConfiguration;
    id: string;
    typeName: string;
    otherChargeLabel?: string;
    freeText?: string;
    toBeCalculatedText?: string;
    checkoutState?: ICheckoutState;
    retailMultiplePickUpOptionEnabled?: boolean;
    channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration;
    isTaxIncludedInPrice?: boolean;
    isShowTaxBreakup?: boolean;
    customerAccountLabel?: string;
}

export interface IOrderSummaryLines {
    subtotal: React.ReactNode;
    shipping?: React.ReactNode;
    otherCharge?: React.ReactNode;
    tax: React.ReactNode;
    orderTotal: React.ReactNode;
    totalDiscounts?: React.ReactNode;
    giftCard?: React.ReactNode;
    loyalty?: React.ReactNode;
    customerAccount?: React.ReactNode;
}

interface IOrderSummaryLineProps {
    label: string;
    context: ICoreContext;
    telemetry: ITelemetry;
    price?: number;
    freeText?: string;
    toBeCalculatedText?: string;
    id: string;
    typeName: string;
    cssLabel: string;
    priceCurrency?: string;
}

interface IShippingOrderLine {
    cart: Cart;
    channelConfiguration: ChannelConfiguration;
    canShip?: boolean;
    hasDeliveryMethod?: boolean;
    hasShippingMethod?: boolean;
    freightFee?: number;
}

const OrderSummaryLine: React.FC<IOrderSummaryLineProps> = ({
    price,
    label,
    context,
    id,
    typeName,
    toBeCalculatedText,
    freeText,
    cssLabel,
    priceCurrency
}) => {
    return (
        <p className={`msc-order-summary__line-${cssLabel}`}>
            <span className="msc-order-summary__label">{label}</span>
            {price || price === 0 ? (
                <PriceComponent
                    data={{
                        price: { CustomerContextualPrice: price }
                    }}
                    freePriceText={freeText}
                    context={context}
                    id={id}
                    typeName={typeName}
                    className="msc-order-summary__value"
                    currencyCode={priceCurrency}
                />
            ) : (
                <span className="msc-order-summary__value">{toBeCalculatedText}</span>
            )}
        </p>
    );
};

const _buildOrderSummarySection = (
    props: IOrderSummaryProps,
    price: number | undefined,
    cssLabel: string,
    label: string = ''
): React.ReactNode => {
    return (
        <OrderSummaryLine
            price={price}
            label={label}
            context={props.context}
            id={props.id}
            typeName={props.typeName}
            toBeCalculatedText={props.toBeCalculatedText}
            freeText={props.freeText}
            telemetry={props.telemetry}
            cssLabel={cssLabel}
            priceCurrency={props.channelConfiguration.Currency}
        />
    );
};

const _computedLoyaltyAmount = (checkoutState: ICheckoutState): number => {
    return (checkoutState && checkoutState.loyaltyAmount) || 0;
};

const _computedCustomerAccountAmount = (checkoutState: ICheckoutState): number => {
    return (checkoutState && checkoutState.customerAccountAmount) || 0;
};

const _computeGiftCardAmount = (checkoutState: ICheckoutState, cart: Cart): number => {
    const giftCardCounter = (count: number, giftCard: IGiftCardExtend) => {
        return count + (giftCard.Balance || 0);
    };
    const giftCardTotalValue = (checkoutState.giftCardExtends || []).reduce(giftCardCounter, 0);
    const amount = (cart.TotalAmount || 0) - _computedLoyaltyAmount(checkoutState);
    return Math.min(giftCardTotalValue, amount);
};

const getDeliveryConfiguration = (
    cart: Cart,
    channelConfiguration: ChannelConfiguration,
    channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration,
    retailMultiplePickUpOptionEnabled?: boolean,
    isTaxIncludedInPrice?: boolean,
    isShowTaxBreakup?: boolean
): IShippingOrderLine => {
    const pickupDeliveryModeCode = channelConfiguration && channelConfiguration.PickupDeliveryModeCode;
    const emailDeliveryModeCode = channelConfiguration && channelConfiguration.EmailDeliveryModeCode;
    const cartLines = cart.CartLines || [];
    const deliveryModes = cartLines.map(cartLine => cartLine.DeliveryMode);
    const taxOnShippingCharge = cart.TaxOnShippingCharge !== undefined ? cart.TaxOnShippingCharge : 0;
    const shippingChargeAmount = cart.ShippingChargeAmount !== undefined ? cart.ShippingChargeAmount : 0;

    // isTaxIncludedInPrice    isShowTaxBreakup     useProperties
    //   False                   False/True             shippingChargeAmount
    //   True                    True                   shippingChargeAmount
    //   True                    false                  shippingChargeAmount + taxOnShippingCharge
    // if either of isTaxIncludedInPrice or isShowTaxBreakup is undefined then we will execute old logic
    let freightFee: number | undefined = 0;
    if (isTaxIncludedInPrice === undefined || isShowTaxBreakup === undefined) {
        freightFee = cart.ShippingChargeAmount;
    } else {
        freightFee = isTaxIncludedInPrice && !isShowTaxBreakup ? shippingChargeAmount + taxOnShippingCharge : cart.ShippingChargeAmount;
    }
    const canShip = deliveryModes.some(
        deliveryMode =>
            !(
                deliveryMode !== '' &&
                (deliveryMode ===
                    getDeliveryMode(deliveryMode, retailMultiplePickUpOptionEnabled, channelDeliveryOptionConfig, pickupDeliveryModeCode) ||
                    deliveryMode === emailDeliveryModeCode)
            )
    );
    const hasDeliveryMethod = deliveryModes.some(deliveryMode => !!deliveryMode && deliveryMode !== emailDeliveryModeCode);
    const hasShippingMethod = deliveryModes.some(
        deliveryMode =>
            !!deliveryMode &&
            deliveryMode !==
                getDeliveryMode(deliveryMode, retailMultiplePickUpOptionEnabled, channelDeliveryOptionConfig, pickupDeliveryModeCode) &&
            deliveryMode !== emailDeliveryModeCode
    );
    return {
        canShip,
        hasDeliveryMethod,
        hasShippingMethod,
        freightFee
    } as IShippingOrderLine;
};

const getDeliveryMode = (
    deliveryMode?: string,
    featureSate: boolean = false,
    channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration,
    pickupDeliveryMode?: string
) => {
    if (!featureSate) {
        return pickupDeliveryMode;
    }
    return channelDeliveryOptionConfig?.PickupDeliveryModeCodes?.find(dM => dM === deliveryMode);
};

// eslint-disable-next-line complexity
export const OrderSummary = (props: IOrderSummaryProps) => {
    const {
        SubtotalAmount,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Existing Property.
        SubtotalAmountWithoutTax,
        TaxAmount,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Existing Property.
        TaxOnNonShippingCharges,
        DiscountAmount = 0,
        TotalAmount,
        OtherChargeAmount
    } = props.cart;

    const { canShip, hasDeliveryMethod, hasShippingMethod, freightFee } = getDeliveryConfiguration(
        props.cart,
        props.channelConfiguration,
        props.channelDeliveryOptionConfig,
        props.retailMultiplePickUpOptionEnabled,
        props.isTaxIncludedInPrice,
        props.isShowTaxBreakup
    );

    let reactNodes: IOrderSummaryLines;
    // if either of isTaxIncludedInPrice or isShowTaxBreakup is undefined then we will execute old logic
    if (props.isTaxIncludedInPrice === undefined || props.isShowTaxBreakup === undefined) {
        reactNodes = {
            subtotal: _buildOrderSummarySection(props, SubtotalAmount, 'sub-total', props.subTotalLabel),
            tax: _buildOrderSummarySection(props, hasDeliveryMethod ? TaxAmount : undefined, 'tax-amount', props.taxLabel),
            orderTotal: _buildOrderSummarySection(props, TotalAmount, 'total', props.orderTotalLabel),
            otherCharge:
                (OtherChargeAmount && _buildOrderSummarySection(props, OtherChargeAmount, 'other-charges', props.otherChargeLabel)) ||
                undefined
        };
    } else {
        const otherChargeAmount = OtherChargeAmount !== undefined ? OtherChargeAmount : 0;
        const taxOnNonShippingCharges = TaxOnNonShippingCharges !== undefined ? TaxOnNonShippingCharges : 0;
        const isTaxGreaterThanZero = TaxAmount !== undefined && TaxAmount > 0;
        // isTaxIncludedInPrice    isShowTaxBreakup     useProperties
        //   False                   False/True             otherChargeAmount
        //   True                    True                   otherChargeAmount
        //   True                    false                  otherChargeAmount+TaxOnNonShippingCharges

        const otherChargeAmountTotal =
            props.isTaxIncludedInPrice && !props.isShowTaxBreakup ? otherChargeAmount + taxOnNonShippingCharges : otherChargeAmount;

        const subtotalAmountActual = props.isTaxIncludedInPrice && props.isShowTaxBreakup ? SubtotalAmountWithoutTax : SubtotalAmount;

        reactNodes = {
            subtotal: _buildOrderSummarySection(props, subtotalAmountActual, 'sub-total', props.subTotalLabel),
            tax: props.isShowTaxBreakup
                ? _buildOrderSummarySection(
                      props,
                      hasDeliveryMethod || isTaxGreaterThanZero ? TaxAmount : undefined,
                      'tax-amount',
                      props.taxLabel
                  )
                : '',
            orderTotal: _buildOrderSummarySection(props, TotalAmount, 'total', props.orderTotalLabel),
            otherCharge:
                (OtherChargeAmount && _buildOrderSummarySection(props, otherChargeAmountTotal, 'other-charges', props.otherChargeLabel)) ||
                undefined
        };
    }
    if (canShip) {
        reactNodes.shipping = _buildOrderSummarySection(props, hasShippingMethod ? freightFee : undefined, 'shipping', props.shippingLabel);
    }
    if (props.checkoutState) {
        const giftCardAmount = _computeGiftCardAmount(props.checkoutState, props.cart);
        const loyaltyAmount = _computedLoyaltyAmount(props.checkoutState);
        const customerAccountAmount = _computedCustomerAccountAmount(props.checkoutState);
        const totalAmountAfterGiftCard = (TotalAmount || 0) - giftCardAmount - loyaltyAmount - customerAccountAmount;

        if (loyaltyAmount > 0) {
            reactNodes.loyalty = _buildOrderSummarySection(props, -loyaltyAmount, 'loyalty', props.loyaltyLabel || 'Loyalty amount');
        }

        if (giftCardAmount > 0) {
            reactNodes.giftCard = _buildOrderSummarySection(props, -giftCardAmount, 'gift-card', props.giftcardLabel || 'Gift card amount');
        }

        if (customerAccountAmount > 0) {
            reactNodes.customerAccount = _buildOrderSummarySection(
                props,
                -customerAccountAmount,
                'customer-credit-amount',
                props.customerAccountLabel || 'Customer Credit amount'
            );
        }

        reactNodes.orderTotal = _buildOrderSummarySection(
            { ...props, freeText: undefined },
            totalAmountAfterGiftCard,
            'total',
            props.orderTotalLabel
        );
    }

    if (DiscountAmount > 0) {
        reactNodes.totalDiscounts = _buildOrderSummarySection(props, -DiscountAmount, 'total-discounts', props.totalDiscountsLabel);
    }

    return reactNodes;
};
